import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Stack, Typography, TextField,
  Switch, FormControlLabel, MenuItem, Button, IconButton, Tooltip,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, CircularProgress,
  Divider, InputAdornment, Chip, Grid, Snackbar,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Settings as SettingsIcon, AccountBalance as SsnitIcon,
  Calculate as PayeIcon, AddCircleOutline as AddIcon,
  DeleteOutline as DelIcon, Save as SaveIcon, Edit as EditIcon,
  RestartAlt as ResetIcon, Groups as DeptIcon, BadgeOutlined as PosIcon,
  Redeem as AllowanceIcon, Payments as PayrollIcon,
  TableChart as BracketsIcon, KeyboardArrowUp as UpIcon,
  KeyboardArrowDown as DownIcon, OpenInNew as ExternalIcon,
  CloudDownload as PresetIcon,
  AccountBalanceWallet as SalaryIcon,
} from '@mui/icons-material';
import API from '../../BaseURL';
import ModuleHeader from '../../components/ModuleHeader';
import swal from '../../utils/swal';

const CCYS = ['GHS', 'USD', 'EUR', 'GBP', 'NGN'];
const GHANA_2024 = [
  { lower_bound: 0,        upper_bound: 490,     rate: 0,    description: 'Tax-free band' },
  { lower_bound: 490.01,   upper_bound: 600,     rate: 5,    description: 'First band' },
  { lower_bound: 600.01,   upper_bound: 730,     rate: 10,   description: 'Second band' },
  { lower_bound: 730.01,   upper_bound: 3896.66, rate: 17.5, description: 'Third band' },
  { lower_bound: 3896.67,  upper_bound: 19896.66,rate: 25,   description: 'Fourth band' },
  { lower_bound: 19896.67, upper_bound: 50416.66,rate: 30,   description: 'Fifth band' },
  { lower_bound: 50416.67, upper_bound: null,    rate: 35,   description: 'Top band' },
];

// Preset library — Ghana PAYE bands gazetted by GRA per year. Each entry
// is a one-click load on the bracket editor. When GRA publishes a new
// table, add a new entry here and ship an app update; the rest of the
// system (settings page, payroll engine) needs no change.
const GHANA_PAYE_PRESETS = [
  {
    key: 'gh-2024', label: 'Ghana 2024 (monthly)',
    notes: 'Per Income Tax (Amendment) Act 2024 — monthly bands',
    rows: GHANA_2024,
  },
  {
    key: 'gh-2023', label: 'Ghana 2023 (monthly)',
    notes: 'Pre-2024 amendment bands — kept for back-dated payroll runs',
    rows: [
      { lower_bound: 0,        upper_bound: 402,     rate: 0,    description: 'Tax-free band' },
      { lower_bound: 402.01,   upper_bound: 512,     rate: 5,    description: 'First band' },
      { lower_bound: 512.01,   upper_bound: 642,     rate: 10,   description: 'Second band' },
      { lower_bound: 642.01,   upper_bound: 3808.33, rate: 17.5, description: 'Third band' },
      { lower_bound: 3808.34,  upper_bound: 41808.33,rate: 25,   description: 'Fourth band' },
      { lower_bound: 41808.34, upper_bound: null,    rate: 30,   description: 'Top band' },
    ],
  },
];

const GRA_URL = 'https://gra.gov.gh/domestic-tax/tax-types/paye/';

// Settings catalog drives the vertical nav rail on the left and the
// section that renders on the right. Adding a new section is one entry.
const SETTING_TABS = [
  { key: 'payroll',    label: 'Payroll',         desc: 'SSNIT, Tier 2, allowance treatment, PAYE base, rounding', icon: <PayrollIcon />,   component: <PayrollSettings /> },
  { key: 'brackets',   label: 'PAYE brackets',   desc: 'Progressive income-tax bands',                              icon: <BracketsIcon />,  component: <PayeBrackets /> },
  { key: 'departments',label: 'Departments',     desc: 'Master list of school departments',                         icon: <DeptIcon />,      component: <DeptList /> },
  { key: 'positions',  label: 'Positions',       desc: 'Master list of staff positions / designations',             icon: <PosIcon />,       component: <PositionList /> },
  { key: 'allowances', label: 'Allowance types', desc: 'Define taxable / pensionable allowances',                   icon: <AllowanceIcon />, component: <AllowanceTypes /> },
];

export default function HRSettingsPage() {
  const [tab, setTab] = useState(0);
  const active = SETTING_TABS[tab];
  return (
    // Page-wide layout — no maxWidth so we use every pixel of the
    // dashboard content area. Tiny outer padding only.
    <Box sx={{ px: { xs: 1, md: 1.5 }, py: 0.5 }}>
      <ModuleHeader
        title="HR Settings"
        subtitle="Payroll rates, PAYE brackets, departments, positions and allowance types"
        icon={<SettingsIcon />}
      />

      {/* Two-column shell: vertical nav rail + section body. The rail
          collapses to a horizontal tab bar on mobile so a phone user
          still gets to every tab. */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 1.5,
        alignItems: 'stretch',
      }}>
        {/* ── Left rail ─────────────────────────────────────────── */}
        <Card sx={{
          width: { xs: '100%', md: 260 },
          flexShrink: 0,
          borderRadius: 2,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          alignSelf: 'flex-start',
        }}>
          <Box sx={{
            px: 2, py: 1.5,
            borderBottom: 1, borderColor: 'divider',
            display: 'flex', alignItems: 'center', gap: 1,
          }}>
            <SettingsIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography sx={{ fontWeight: 800, fontSize: 12, textTransform: 'uppercase',
              letterSpacing: 0.7, color: 'text.secondary' }}>
              Sections
            </Typography>
          </Box>
          <Box sx={{ p: 0.75 }}>
            {SETTING_TABS.map((s, i) => {
              const isActive = i === tab;
              return (
                <Box key={s.key}
                  onClick={() => setTab(i)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setTab(i); }}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.2,
                    px: 1.5, py: 1.1, mb: 0.25,
                    borderRadius: 1.5,
                    cursor: 'pointer',
                    position: 'relative',
                    bgcolor: isActive ? (t) => alpha(t.palette.primary.main, 0.10) : 'transparent',
                    color: isActive ? 'primary.main' : 'text.primary',
                    transition: 'background 0.12s',
                    '&:hover': {
                      bgcolor: isActive
                        ? (t) => alpha(t.palette.primary.main, 0.14)
                        : (t) => alpha(t.palette.primary.main, 0.05),
                    },
                    ...(isActive && {
                      '&::before': {
                        content: '""', position: 'absolute', left: 0, top: '20%', bottom: '20%',
                        width: 3, borderRadius: '0 3px 3px 0', bgcolor: 'primary.main',
                      },
                    }),
                  }}>
                  <Box sx={{
                    width: 32, height: 32, borderRadius: 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: isActive ? 'primary.main' : (t) => alpha(t.palette.primary.main, 0.08),
                    color:   isActive ? '#fff' : 'primary.main',
                    flexShrink: 0,
                  }}>
                    {s.icon}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{
                      fontWeight: isActive ? 800 : 600,
                      fontSize: 13.5, lineHeight: 1.2,
                    }} noWrap>
                      {s.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap
                      sx={{ display: { xs: 'none', md: 'block' }, fontSize: 10.5, mt: 0.2 }}>
                      {s.desc}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Card>

        {/* ── Right pane ────────────────────────────────────────── */}
        <Card sx={{
          flex: 1, minWidth: 0,
          borderRadius: 2,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}>
          <Box sx={{
            px: 2.5, py: 1.5,
            borderBottom: 1, borderColor: 'divider',
            display: 'flex', alignItems: 'center', gap: 1,
            bgcolor: '#fafbfc',
          }}>
            {active.icon}
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, fontSize: 14.5, lineHeight: 1.2 }}>
                {active.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {active.desc}
              </Typography>
            </Box>
          </Box>
          <CardContent sx={{ p: { xs: 1.5, md: 2.5 } }}>
            {active.component}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

// ──────────────────────────────────────────────────────────────────
// Tab 1 — Payroll scalar settings (SSNIT, Tier 2, allowances, PAYE base)
// ──────────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────────
// Payroll settings — full pro panel
// 1. Active-config strip      → at-a-glance chips of what's set
// 2. Worked sample preview    → live computation against a 5k+1k staff
// 3. Settings grid             → 5 cards side by side / 2-up
// 4. Sticky save bar          → status indicator + dirty-state hint
// ──────────────────────────────────────────────────────────────────
function PayrollSettings() {
  const [settings, setSettings] = useState(null);
  const [original, setOriginal] = useState(null);   // for dirty detection
  const [brackets, setBrackets] = useState([]);     // needed for live preview
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [toast, setToast]       = useState(null);   // { msg, severity } | null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await API.get('/hr/settings');
      const s = r.data.settings || {};
      setSettings(s);
      setOriginal(JSON.stringify(s));
      setBrackets(r.data.brackets || []);
    } catch (e) {
      setToast({ msg: 'Could not load settings', severity: 'error' });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setSettings(s => ({ ...s, [k]: v }));

  const dirty = settings && original && JSON.stringify(settings) !== original;

  const save = async () => {
    setSaving(true);
    try {
      await API.put('/hr/settings', { settings, brackets });
      setOriginal(JSON.stringify(settings));
      setToast({ msg: 'Settings saved', severity: 'success' });
    } catch (e) {
      setToast({ msg: e.response?.data?.message || 'Save failed', severity: 'error' });
    } finally { setSaving(false); }
  };

  // Worked sample — recomputed every render so the operator sees the
  // impact of every toggle / rate change live.
  const sample = useMemo(() => {
    if (!settings) return null;
    return computeSample(5000, 1000, settings, brackets);
  }, [settings, brackets]);

  if (loading) return (
    <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
  );
  if (!settings) return <Alert severity="error">Could not load settings.</Alert>;

  return (
    <Box>
      {/* ── Active-config strip ─────────────────────────────── */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 2,
        bgcolor: (t) => alpha(t.palette.primary.main, 0.03) }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <SettingsIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography sx={{ fontWeight: 800, fontSize: 12, textTransform: 'uppercase',
              letterSpacing: 0.7, color: 'text.secondary' }}>
              Active configuration
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
            <ConfigChip label="Currency" value={settings.currency || 'GHS'} />
            <ConfigChip label="SSNIT" tone={settings.ssnit_enabled ? 'success' : 'default'}
              value={settings.ssnit_enabled
                ? `${num(settings.ssnit_employee_rate)}% / ${num(settings.ssnit_employer_rate)}%`
                : 'OFF'} />
            <ConfigChip label="Tier 2" tone={settings.tier2_enabled ? 'info' : 'default'}
              value={settings.tier2_enabled
                ? `${num(settings.tier2_employee_rate)}% / ${num(settings.tier2_employer_rate)}%`
                : 'OFF'} />
            <ConfigChip label="PAYE base"
              value={settings.paye_base === 'gross' ? 'Gross' : 'Gross − SSNIT'} />
            <ConfigChip label="Allowances"
              value={`${settings.allowances_taxable ? 'taxable' : 'not taxable'} · ${settings.allowances_pensionable ? 'pensionable' : 'not pensionable'}`} />
            <ConfigChip label="Round net to" value={`GHS ${num(settings.round_net_to, 0.01).toFixed(2)}`} />
          </Stack>
        </CardContent>
      </Card>

      {/* ── Live worked sample ──────────────────────────────── */}
      {sample && (
        <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
          <Box sx={{ px: 2, py: 1.2, borderBottom: 1, borderColor: 'divider',
            display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fafbfc' }}>
            <PayeIcon sx={{ fontSize: 18, color: 'success.main' }} />
            <Box>
              <Typography fontWeight={800} fontSize={14}>Worked sample</Typography>
              <Typography variant="caption" color="text.secondary">
                Basic GHS 5,000 + Allowances GHS 1,000 → what an employee actually receives
              </Typography>
            </Box>
          </Box>
          <Grid container sx={{ p: 2 }} spacing={1.2}>
            <SampleTile label="Gross"            value={sample.gross}  tone="primary" />
            <SampleTile label="SSNIT (employee)" value={sample.ssnit}  tone="error" minus />
            <SampleTile label="PAYE"             value={sample.paye}   tone="error" minus />
            <SampleTile label="Total deductions" value={sample.ded}    tone="warning" minus />
            <SampleTile label="NET PAY"          value={sample.net}    tone="success" bold />
            <SampleTile label="SSNIT (employer)" value={sample.ssnitEmployer} tone="info"
              note="Not deducted" />
          </Grid>
        </Card>
      )}

      {/* ── Settings grid ───────────────────────────────────── */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <SettingCard title="Currency & rounding" icon={<SettingsIcon />}
            subtitle="Default currency and how net pay gets rounded">
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField select size="small" label="Currency" value={settings.currency || 'GHS'}
                onChange={(e) => setF('currency', e.target.value)} sx={{ flex: 1 }}>
                {CCYS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
              <TextField size="small" type="number" label="Round net to" value={settings.round_net_to ?? 0.01}
                onChange={(e) => setF('round_net_to', e.target.value)} sx={{ flex: 1 }}
                inputProps={{ min: 0.01, step: '0.01' }}
                helperText="0.01 = cent · 1.00 = nearest cedi" />
            </Stack>
          </SettingCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SettingCard title="PAYE calculation" icon={<PayeIcon />}
            subtitle="Which amount the tax brackets walk against">
            <TextField select size="small" label="PAYE base"
              value={settings.paye_base || 'gross_minus_ssnit'}
              onChange={(e) => setF('paye_base', e.target.value)}
              fullWidth>
              <MenuItem value="gross_minus_ssnit">Gross − SSNIT (Ghana default)</MenuItem>
              <MenuItem value="gross">Gross (no SSNIT deduction first)</MenuItem>
            </TextField>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.6, display: 'block' }}>
              Ghana standard: PAYE is computed after SSNIT is deducted from the gross.
            </Typography>
          </SettingCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SettingCard title="SSNIT (Tier 1)" icon={<SsnitIcon />}
            subtitle="Social Security & National Insurance Trust"
            toggle={
              <Switch checked={!!settings.ssnit_enabled}
                onChange={(e) => setF('ssnit_enabled', e.target.checked)} />
            }>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <RateField label="Employee rate" value={settings.ssnit_employee_rate}
                onChange={(v) => setF('ssnit_employee_rate', v)} disabled={!settings.ssnit_enabled} />
              <RateField label="Employer rate" value={settings.ssnit_employer_rate}
                onChange={(v) => setF('ssnit_employer_rate', v)} disabled={!settings.ssnit_enabled} />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.6, display: 'block' }}>
              Default in Ghana: 5.5% employee · 13.0% employer.
            </Typography>
          </SettingCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SettingCard title="Tier 2 pension" icon={<SsnitIcon />} tone="info"
            subtitle="Optional second-tier private pension scheme"
            toggle={
              <Switch checked={!!settings.tier2_enabled}
                onChange={(e) => setF('tier2_enabled', e.target.checked)} />
            }>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <RateField label="Employee rate" value={settings.tier2_employee_rate}
                onChange={(v) => setF('tier2_employee_rate', v)} disabled={!settings.tier2_enabled} />
              <RateField label="Employer rate" value={settings.tier2_employer_rate}
                onChange={(v) => setF('tier2_employer_rate', v)} disabled={!settings.tier2_enabled} />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.6, display: 'block' }}>
              Only applied when enabled — adds to the SSNIT deduction.
            </Typography>
          </SettingCard>
        </Grid>

        <Grid item xs={12}>
          <SettingCard title="Aggregate-allowance fallback" icon={<PayeIcon />} tone="warning"
            subtitle="Used when an employee has NO itemised allowances. Itemised types (set under Allowance types) always win per-row.">
            <Grid container spacing={1.2}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 1.2, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                  <FormControlLabel control={<Switch checked={!!settings.allowances_taxable}
                    onChange={(e) => setF('allowances_taxable', e.target.checked)} />}
                    label={
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>Taxable</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Aggregate allowances are added to the PAYE base
                        </Typography>
                      </Box>
                    } />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 1.2, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                  <FormControlLabel control={<Switch checked={!!settings.allowances_pensionable}
                    onChange={(e) => setF('allowances_pensionable', e.target.checked)} />}
                    label={
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>Pensionable</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Aggregate allowances are added to the SSNIT base
                        </Typography>
                      </Box>
                    } />
                </Box>
              </Grid>
            </Grid>
          </SettingCard>
        </Grid>
      </Grid>

      {/* ── Sticky save bar ─────────────────────────────────── */}
      <Box sx={{
        position: 'sticky', bottom: 0, mt: 2, mx: -1,
        px: 2, py: 1.2,
        bgcolor: 'background.paper',
        borderTop: 2,
        borderColor: dirty ? 'warning.main' : 'divider',
        display: 'flex', alignItems: 'center', gap: 1.5,
        boxShadow: '0 -4px 12px rgba(0,0,0,0.04)',
        borderRadius: '0 0 8px 8px',
      }}>
        <Box sx={{
          width: 10, height: 10, borderRadius: '50%',
          bgcolor: dirty ? 'warning.main' : 'success.main',
          flexShrink: 0,
        }} />
        <Typography variant="body2" sx={{ fontWeight: 700, color: dirty ? 'warning.dark' : 'text.secondary' }}>
          {dirty ? 'Unsaved changes' : 'All settings saved'}
        </Typography>
        {settings.updated_by && !dirty && (
          <Typography variant="caption" color="text.secondary">
            · Last updated by {settings.updated_by}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={load} disabled={saving} sx={{ textTransform: 'none' }}>
          Discard
        </Button>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={save}
          disabled={saving || !dirty}
          sx={{ textTransform: 'none', fontWeight: 800, px: 3 }}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </Box>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast?.severity || 'info'} variant="filled" onClose={() => setToast(null)}>
          {toast?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ──────────────────────────────────────────────────────────────────
// Helpers for the Payroll panel
// ──────────────────────────────────────────────────────────────────
const num = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};
const fmtGhs = (n) => `GHS ${Number(n || 0).toLocaleString('en-GH',
  { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Pure replica of backend buildPayslip / computePaye — kept tiny.
function computeSample(basic, allowances, s, brackets) {
  const taxable = basic + (s.allowances_taxable ? allowances : 0);
  const pension = basic + (s.allowances_pensionable ? allowances : 0);
  const ssnitRate = num(s.ssnit_employee_rate);
  const ssnitERate = num(s.ssnit_employer_rate);
  const ssnit  = s.ssnit_enabled ? pension * (ssnitRate  / 100) : 0;
  const ssnitEmployer = s.ssnit_enabled ? pension * (ssnitERate / 100) : 0;
  const payeBase = s.paye_base === 'gross' ? taxable : Math.max(0, taxable - ssnit);
  let paye = 0;
  for (const b of (brackets || [])) {
    const lb = num(b.lower_bound);
    const ub = b.upper_bound == null ? Infinity : num(b.upper_bound);
    if (payeBase <= lb) break;
    const slice = Math.min(payeBase, ub) - lb;
    if (slice > 0) paye += slice * (num(b.rate) / 100);
  }
  const ded = ssnit + paye;
  const net = basic + allowances - ded;
  return {
    gross: basic + allowances,
    ssnit, ssnitEmployer, paye, ded, net,
  };
}

function ConfigChip({ label, value, tone = 'primary' }) {
  return (
    <Chip size="small"
      label={<>
        <Box component="span" sx={{ fontWeight: 600, opacity: 0.75, mr: 0.6 }}>{label}:</Box>
        <Box component="span" sx={{ fontWeight: 800 }}>{value}</Box>
      </>}
      sx={{
        height: 24, fontSize: 11.5,
        bgcolor: (t) => alpha(t.palette[tone === 'default' ? 'grey' : tone]?.main || t.palette.grey[600], 0.12),
        color: tone === 'default' ? 'text.disabled' : `${tone}.dark`,
        border: '1px solid',
        borderColor: (t) => alpha(t.palette[tone === 'default' ? 'grey' : tone]?.main || t.palette.grey[600], 0.25),
      }} />
  );
}

function SampleTile({ label, value, tone = 'primary', minus, bold, note }) {
  return (
    <Grid item xs={6} sm={4} md={2}>
      <Box sx={{ p: 1.2, borderRadius: 1.5,
        border: '1px solid', borderColor: 'divider',
        bgcolor: (t) => alpha(t.palette[tone].main, bold ? 0.10 : 0.04),
      }}>
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', fontSize: 10 }}>
          {label}
        </Typography>
        <Typography sx={{ fontWeight: 800, fontSize: bold ? 16 : 14,
          color: `${tone}.dark`, lineHeight: 1.2, mt: 0.2 }}>
          {minus ? '− ' : ''}{fmtGhs(value)}
        </Typography>
        {note && (
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 9.5 }}>
            {note}
          </Typography>
        )}
      </Box>
    </Grid>
  );
}

function SettingCard({ title, subtitle, icon, tone = 'primary', toggle, children }) {
  return (
    <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
      <Box sx={{ px: 2, py: 1.2, borderBottom: 1, borderColor: 'divider',
        display: 'flex', alignItems: 'center', gap: 1.2, bgcolor: '#fafbfc' }}>
        <Box sx={{
          width: 34, height: 34, borderRadius: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: (t) => alpha(t.palette[tone].main, 0.12),
          color: `${tone}.main`, flexShrink: 0,
        }}>
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight={800} fontSize={13.5} lineHeight={1.2}>{title}</Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5, display: 'block' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {toggle}
      </Box>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Card>
  );
}

function RateField({ label, value, onChange, disabled }) {
  return (
    <TextField size="small" type="number" label={label} value={value ?? ''}
      onChange={(e) => onChange(e.target.value)} disabled={disabled}
      sx={{ flex: 1 }} inputProps={{ min: 0, step: '0.01' }}
      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
  );
}

// ──────────────────────────────────────────────────────────────────
// Tab 2 — PAYE brackets editor (replace-all on save)
// ──────────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────────
// PAYE brackets — full pro panel
// 1. Header stats             → bands · tax-free · top rate
// 2. Preset library           → load Ghana 2024, 2023…
// 3. Live PAYE calculator     → enter taxable, see slice-by-slice
// 4. Bracket editor table     → polished rows + up/down + delete
// 5. Sticky save bar          → dirty state + Save / Discard
// ──────────────────────────────────────────────────────────────────
function PayeBrackets() {
  const [settings, setSettings] = useState(null);
  const [rows, setRows]         = useState([]);
  const [original, setOriginal] = useState('');
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [toast, setToast]       = useState(null);
  const [tryTax, setTryTax]     = useState(5000);   // live-calculator input
  const [preset, setPreset]     = useState('');     // selected preset key

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await API.get('/hr/settings');
      setSettings(r.data.settings || {});
      setRows(r.data.brackets || []);
      setOriginal(JSON.stringify(r.data.brackets || []));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const dirty = JSON.stringify(rows) !== original;

  const setRow = (i, k, v) => setRows(rs => rs.map((r, j) => j === i ? { ...r, [k]: v } : r));
  const addRow = () => setRows(rs => {
    const last = rs[rs.length - 1];
    const nextLower = last
      ? (last.upper_bound == null ? Number(last.lower_bound) + 1 : Number(last.upper_bound) + 0.01)
      : 0;
    return [...rs, {
      lower_bound: Math.round(nextLower * 100) / 100,
      upper_bound: null, rate: 0, description: '',
    }];
  });
  const delRow = (i) => setRows(rs => rs.filter((_, j) => j !== i));
  const moveRow = (i, dir) => {
    setRows(rs => {
      const next = [...rs];
      const j = i + dir;
      if (j < 0 || j >= next.length) return rs;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const loadPreset = (key) => {
    setPreset(key);
    const p = GHANA_PAYE_PRESETS.find(x => x.key === key);
    if (p) setRows(p.rows.map(r => ({ ...r })));
  };

  const save = async () => {
    setSaving(true);
    try {
      const clean = rows
        .map(r => ({ ...r,
          lower_bound: Number(r.lower_bound) || 0,
          upper_bound: r.upper_bound === '' || r.upper_bound == null ? null : Number(r.upper_bound),
          rate: Number(r.rate) || 0,
        }))
        .filter(r => Number.isFinite(r.lower_bound));
      if (clean.length === 0) {
        setToast({ msg: 'At least one bracket is required', severity: 'error' });
        return;
      }
      await API.put('/hr/settings', { settings, brackets: clean });
      setOriginal(JSON.stringify(clean));
      setRows(clean);
      setToast({ msg: 'Bands saved', severity: 'success' });
    } catch (e) {
      setToast({ msg: e.response?.data?.message || 'Save failed', severity: 'error' });
    } finally { setSaving(false); }
  };

  // Stats for the header strip.
  const stats = useMemo(() => {
    const taxFree = (rows.find(r => Number(r.rate) === 0)?.upper_bound) || 0;
    const topRate = rows.reduce((m, r) => Math.max(m, Number(r.rate || 0)), 0);
    return { bands: rows.length, taxFree: Number(taxFree), topRate };
  }, [rows]);

  // Live PAYE calculator — walks the current (unsaved) bands.
  const sim = useMemo(() => walkBrackets(Number(tryTax) || 0, rows), [tryTax, rows]);

  if (loading) return (
    <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
  );

  return (
    <Box>
      {/* ── Header stats strip ──────────────────────────────── */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 2,
        bgcolor: (t) => alpha(t.palette.primary.main, 0.03) }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <BracketsIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography sx={{ fontWeight: 800, fontSize: 12, textTransform: 'uppercase',
              letterSpacing: 0.7, color: 'text.secondary' }}>
              Current PAYE configuration
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Tooltip title="Open GRA's PAYE page in a new tab — verify the gazetted bands">
              <Button size="small" endIcon={<ExternalIcon sx={{ fontSize: 14 }} />}
                onClick={() => window.open(GRA_URL, '_blank', 'noopener,noreferrer')}
                sx={{ textTransform: 'none', fontWeight: 700 }}>
                Check GRA site
              </Button>
            </Tooltip>
          </Stack>
          <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
            <ConfigChip label="Bands"        value={String(stats.bands)} />
            <ConfigChip label="Tax-free up to" value={fmtGhs(stats.taxFree)}
              tone={stats.taxFree > 0 ? 'success' : 'default'} />
            <ConfigChip label="Top rate"     value={`${stats.topRate}%`}
              tone={stats.topRate >= 30 ? 'warning' : 'primary'} />
          </Stack>
        </CardContent>
      </Card>

      {/* ── Preset library ──────────────────────────────────── */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
        <Box sx={{ px: 2, py: 1.2, borderBottom: 1, borderColor: 'divider',
          display: 'flex', alignItems: 'center', gap: 1.2, bgcolor: '#fafbfc' }}>
          <Box sx={{
            width: 34, height: 34, borderRadius: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: (t) => alpha(t.palette.info.main, 0.12),
            color: 'info.main', flexShrink: 0,
          }}><PresetIcon /></Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography fontWeight={800} fontSize={13.5}>Preset library</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5, display: 'block' }}>
              GRA doesn't publish a JSON API — load a known gazetted year and tweak from there.
              New presets ship in app updates as GRA publishes new bands.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
            <TextField select size="small" label="Load gazetted preset"
              value={preset}
              onChange={(e) => loadPreset(e.target.value)}
              sx={{ minWidth: 280 }}>
              <MenuItem value=""><em>Pick a year…</em></MenuItem>
              {GHANA_PAYE_PRESETS.map(p =>
                <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>
              )}
            </TextField>
            {preset && (
              <Typography variant="caption" color="text.secondary">
                {GHANA_PAYE_PRESETS.find(p => p.key === preset)?.notes}
              </Typography>
            )}
          </Stack>
        </Box>
      </Card>

      {/* ── Live PAYE calculator ─────────────────────────────── */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
        <Box sx={{ px: 2, py: 1.2, borderBottom: 1, borderColor: 'divider',
          display: 'flex', alignItems: 'center', gap: 1.2, bgcolor: '#fafbfc' }}>
          <Box sx={{
            width: 34, height: 34, borderRadius: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: (t) => alpha(t.palette.success.main, 0.12),
            color: 'success.main', flexShrink: 0,
          }}><PayeIcon /></Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography fontWeight={800} fontSize={13.5}>Live PAYE calculator</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5, display: 'block' }}>
              Try a taxable amount against the bands below — including any edits you haven't saved yet.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField size="small" type="number" label="Taxable amount (GHS)"
              value={tryTax} onChange={(e) => setTryTax(e.target.value)}
              sx={{ minWidth: 220 }}
              InputProps={{ startAdornment: <InputAdornment position="start">GHS</InputAdornment> }} />
            <Box sx={{ flex: 1 }}>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 800, fontSize: 10.5, textTransform: 'uppercase', bgcolor: '#f7f9fc' } }}>
                      <TableCell>Band</TableCell>
                      <TableCell align="right">Slice in this band</TableCell>
                      <TableCell align="right">Rate</TableCell>
                      <TableCell align="right">Tax</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sim.steps.length === 0 && (
                      <TableRow><TableCell colSpan={4} align="center"
                        sx={{ color: 'text.disabled', fontStyle: 'italic', py: 2 }}>
                        Amount falls within the tax-free band — no PAYE.
                      </TableCell></TableRow>
                    )}
                    {sim.steps.map((st, i) => (
                      <TableRow key={i} hover>
                        <TableCell sx={{ fontWeight: 700 }}>{st.label}</TableCell>
                        <TableCell align="right">{fmtGhs(st.slice)}</TableCell>
                        <TableCell align="right">{st.rate.toFixed(2)} %</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtGhs(st.tax)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ '& td': { borderTop: 2, borderColor: 'divider', fontWeight: 800, bgcolor: '#f7f9fc' } }}>
                      <TableCell colSpan={3} align="right">TOTAL PAYE</TableCell>
                      <TableCell align="right" sx={{ color: 'error.main', fontSize: 14 }}>{fmtGhs(sim.total)}</TableCell>
                    </TableRow>
                    <TableRow sx={{ '& td': { fontWeight: 700, bgcolor: '#fafbfc', borderBottom: 0 } }}>
                      <TableCell colSpan={3} align="right">Effective rate</TableCell>
                      <TableCell align="right">
                        {sim.effective.toFixed(2)} %
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Stack>
        </Box>
      </Card>

      {/* ── Bracket editor table ─────────────────────────────── */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
        <Box sx={{ px: 2, py: 1.2, borderBottom: 1, borderColor: 'divider',
          display: 'flex', alignItems: 'center', gap: 1.2, bgcolor: '#fafbfc' }}>
          <Box sx={{
            width: 34, height: 34, borderRadius: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
            color: 'primary.main', flexShrink: 0,
          }}><BracketsIcon /></Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography fontWeight={800} fontSize={13.5}>PAYE bands</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5, display: 'block' }}>
              Edit inline. Bands walk bottom-up — each row taxes only the slice that lands in its range.
              Leave the last row's Upper blank to mean infinity.
            </Typography>
          </Box>
          <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={addRow}
            sx={{ textTransform: 'none', fontWeight: 700 }}>
            Add band
          </Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 800, fontSize: 10.5, textTransform: 'uppercase',
                color: 'text.secondary', bgcolor: '#fdfdfd' } }}>
                <TableCell sx={{ width: 60 }}>Order</TableCell>
                <TableCell align="right" sx={{ width: 140 }}>Lower (GHS)</TableCell>
                <TableCell align="right" sx={{ width: 160 }}>Upper (blank = ∞)</TableCell>
                <TableCell align="right" sx={{ width: 110 }}>Rate %</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right" sx={{ width: 120 }}>Reorder</TableCell>
                <TableCell align="right" sx={{ width: 50 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center"
                  sx={{ color: 'text.disabled', fontStyle: 'italic', py: 4 }}>
                  No bands yet. Load a preset above or click <b>Add band</b>.
                </TableCell></TableRow>
              )}
              {rows.map((r, i) => (
                <TableRow key={i} hover>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>{i + 1}</TableCell>
                  <TableCell align="right">
                    <TextField size="small" type="number" value={r.lower_bound ?? ''}
                      onChange={(e) => setRow(i, 'lower_bound', e.target.value)}
                      inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }}
                      sx={{ width: 130 }} />
                  </TableCell>
                  <TableCell align="right">
                    <TextField size="small" type="number" value={r.upper_bound ?? ''}
                      onChange={(e) => setRow(i, 'upper_bound', e.target.value)}
                      placeholder="∞"
                      inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }}
                      sx={{ width: 150 }} />
                  </TableCell>
                  <TableCell align="right">
                    <TextField size="small" type="number" value={r.rate ?? ''}
                      onChange={(e) => setRow(i, 'rate', e.target.value)}
                      inputProps={{ min: 0, max: 100, step: '0.01', style: { textAlign: 'right' } }}
                      sx={{ width: 100 }}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
                  </TableCell>
                  <TableCell>
                    <TextField size="small" value={r.description ?? ''}
                      onChange={(e) => setRow(i, 'description', e.target.value)} fullWidth
                      placeholder="e.g. Top band" />
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <IconButton size="small" disabled={i === 0} onClick={() => moveRow(i, -1)}>
                      <UpIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" disabled={i === rows.length - 1} onClick={() => moveRow(i, +1)}>
                      <DownIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Remove band">
                      <IconButton size="small" color="error" onClick={() => delRow(i)}>
                        <DelIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* ── Sticky save bar ─────────────────────────────────── */}
      <Box sx={{
        position: 'sticky', bottom: 0, mt: 2, mx: -1,
        px: 2, py: 1.2, bgcolor: 'background.paper',
        borderTop: 2, borderColor: dirty ? 'warning.main' : 'divider',
        display: 'flex', alignItems: 'center', gap: 1.5,
        boxShadow: '0 -4px 12px rgba(0,0,0,0.04)',
        borderRadius: '0 0 8px 8px',
      }}>
        <Box sx={{
          width: 10, height: 10, borderRadius: '50%',
          bgcolor: dirty ? 'warning.main' : 'success.main', flexShrink: 0,
        }} />
        <Typography variant="body2" sx={{ fontWeight: 700, color: dirty ? 'warning.dark' : 'text.secondary' }}>
          {dirty ? 'Unsaved changes' : 'All bands saved'}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button onClick={load} disabled={saving} sx={{ textTransform: 'none' }}>Discard</Button>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={save}
          disabled={saving || !dirty}
          sx={{ textTransform: 'none', fontWeight: 800, px: 3 }}>
          {saving ? 'Saving…' : 'Save bands'}
        </Button>
      </Box>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast?.severity || 'info'} variant="filled" onClose={() => setToast(null)}>
          {toast?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// Walks the bracket array bottom-up against a taxable amount, returning
// every slice's tax + the total. Mirrors the backend computePaye.
function walkBrackets(taxable, brackets) {
  const steps = [];
  let total = 0;
  for (let i = 0; i < brackets.length; i++) {
    const b = brackets[i];
    const lb = Number(b.lower_bound) || 0;
    const ub = b.upper_bound == null || b.upper_bound === '' ? Infinity : Number(b.upper_bound);
    const rate = Number(b.rate) || 0;
    if (taxable <= lb) break;
    const slice = Math.min(taxable, ub) - lb;
    if (slice <= 0) continue;
    if (rate === 0) continue;     // tax-free, no row needed
    const tax = slice * rate / 100;
    total += tax;
    steps.push({
      label: b.description || `Band ${i + 1}`,
      slice, rate, tax,
    });
  }
  const effective = taxable > 0 ? (total / taxable) * 100 : 0;
  return { steps, total, effective };
}

// ──────────────────────────────────────────────────────────────────
// Tabs 3 + 4 — Departments and Positions
// Pro inline-edit panel with search, sticky save bar, snackbar.
// ──────────────────────────────────────────────────────────────────
function DeptList()    { return <MasterListPro resource="departments" title="Departments" singular="department" icon={<DeptIcon />} />; }
function PositionList(){ return <MasterListPro resource="positions"   title="Positions"   singular="position"   icon={<PosIcon />} withDepartment />; }

function MasterListPro({ resource, title, singular, icon, withDepartment }) {
  const [rows, setRows]       = useState([]);
  const [original, setOrig]   = useState('[]');
  const [depts, setDepts]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [toast, setToast]     = useState(null);
  const [q, setQ]             = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await API.get(`/hr/${resource}`, { params: { includeInactive: 1 } });
      const list = (r.data || []).map((row, i) => ({
        ...row,
        notes: row.notes || '',
        // Stable row key so React doesn't lose focus mid-edit.
        _key: `db-${row.id}`,
        // Snapshot fields the diff needs.
        _orig: {
          name: row.name, notes: row.notes || '',
          dept_id: row.dept_id || null,
          is_active: row.is_active,
        },
      }));
      setRows(list);
      setOrig(JSON.stringify(list.map(stripVolatile)));
      if (withDepartment) {
        const d = await API.get('/hr/departments');
        setDepts(d.data || []);
      }
    } catch (e) {
      setToast({ msg: 'Could not load', severity: 'error' });
    } finally { setLoading(false); }
  }, [resource, withDepartment]);
  useEffect(() => { load(); }, [load]);

  const setRow = (key, patch) => setRows(rs => rs.map(r => r._key === key ? { ...r, ...patch } : r));
  const removeRow = (key) => setRows(rs => rs.filter(r => r._key !== key));
  const addRow = () => {
    const _key = `new-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    setRows(rs => [...rs, {
      id: null, _key, name: '', notes: '', dept_id: null, is_active: 1,
      _orig: null,    // marks as "new"
    }]);
  };

  // Filter (does not affect the underlying state, only what we render).
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter(r =>
      (r.name || '').toLowerCase().includes(t) ||
      (r.notes || '').toLowerCase().includes(t) ||
      (r.department || '').toLowerCase().includes(t)
    );
  }, [rows, q]);

  // Dirty detection — covers added, edited, and deleted rows.
  const dirty = JSON.stringify(rows.map(stripVolatile)) !== original;

  const save = async () => {
    setSaving(true);
    try {
      // Walk the snapshot to figure out deletions.
      const origList = JSON.parse(original);
      const currentIds = new Set(rows.filter(r => r.id).map(r => r.id));
      const deleted = origList.filter(r => r.id && !currentIds.has(r.id));

      // 1) Inserts (no id, has name)
      for (const r of rows) {
        if (r.id) continue;
        const name = r.name?.trim();
        if (!name) continue;
        const body = withDepartment
          ? { name, dept_id: r.dept_id || null, notes: r.notes || null }
          : { name, notes: r.notes || null };
        await API.post(`/hr/${resource}`, body);
      }

      // 2) Updates (existing id, fields changed)
      for (const r of rows) {
        if (!r.id || !r._orig) continue;
        const changed =
          (r.name || '')      !== (r._orig.name || '')      ||
          (r.notes || '')     !== (r._orig.notes || '')     ||
          (r.dept_id || null) !== (r._orig.dept_id || null) ||
          (!!r.is_active)     !== (!!r._orig.is_active);
        if (!changed) continue;
        const body = withDepartment
          ? { name: r.name?.trim() || null, dept_id: r.dept_id || null,
              notes: r.notes || null, is_active: !!r.is_active }
          : { name: r.name?.trim() || null, notes: r.notes || null,
              is_active: !!r.is_active };
        await API.put(`/hr/${resource}/${r.id}`, body);
      }

      // 3) Deletes (archive)
      for (const r of deleted) {
        await API.delete(`/hr/${resource}/${r.id}`);
      }

      setToast({ msg: 'Saved', severity: 'success' });
      await load();
    } catch (e) {
      setToast({ msg: e.response?.data?.message || 'Save failed', severity: 'error' });
    } finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  const activeCount   = rows.filter(r => r.is_active && r.id).length;
  const archivedCount = rows.filter(r => !r.is_active).length;
  const newCount      = rows.filter(r => !r.id).length;
  const tone = withDepartment ? 'secondary' : 'primary';

  return (
    <Box>
      {/* ── Header stats strip ──────────────────────────────── */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 2,
        bgcolor: (t) => alpha(t.palette[tone].main, 0.03) }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ color: `${tone}.main`, display: 'flex' }}>{icon}</Box>
            <Typography sx={{ fontWeight: 800, fontSize: 12, textTransform: 'uppercase',
              letterSpacing: 0.7, color: 'text.secondary' }}>
              {title} master list
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Stack direction="row" spacing={0.8}>
              <ConfigChip label="Active"   value={String(activeCount)}   tone="success" />
              {!!archivedCount && <ConfigChip label="Archived" value={String(archivedCount)} tone="default" />}
              {!!newCount      && <ConfigChip label="New"      value={String(newCount)}      tone="info" />}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* ── Toolbar ─────────────────────────────────────────── */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
        <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5,
          borderBottom: 1, borderColor: 'divider', bgcolor: '#fafbfc' }}>
          <TextField size="small" placeholder={`Search ${singular}s…`}
            value={q} onChange={(e) => setQ(e.target.value)}
            sx={{ flex: 1, maxWidth: 360 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><BracketsIcon sx={{ fontSize: 16, color: 'text.disabled' }} /></InputAdornment>,
              endAdornment: q ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setQ('')}><DelIcon sx={{ fontSize: 14 }} /></IconButton>
                </InputAdornment>
              ) : null,
            }} />
          <Box sx={{ flex: 1 }} />
          <Button startIcon={<AddIcon />} variant="contained" onClick={addRow}
            sx={{ textTransform: 'none', fontWeight: 800 }}>
            Add {singular}
          </Button>
        </Box>

        {/* ── Inline-edit table ─────────────────────────────── */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 800, fontSize: 10.5, textTransform: 'uppercase',
                color: 'text.secondary', bgcolor: '#fdfdfd' } }}>
                <TableCell sx={{ width: 48 }}>#</TableCell>
                <TableCell sx={{ minWidth: 200 }}>Name</TableCell>
                {withDepartment && <TableCell sx={{ minWidth: 180 }}>Department</TableCell>}
                <TableCell>Notes</TableCell>
                <TableCell align="center" sx={{ width: 90 }}>Active</TableCell>
                <TableCell align="right" sx={{ width: 56 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={withDepartment ? 6 : 5} align="center"
                  sx={{ color: 'text.disabled', fontStyle: 'italic', py: 4 }}>
                  {q ? `No ${singular}s match "${q}".` : `No ${singular}s yet — click Add ${singular}.`}
                </TableCell></TableRow>
              )}
              {filtered.map((r, i) => {
                const isNew = !r.id;
                return (
                  <TableRow key={r._key} hover sx={{
                    bgcolor: isNew ? (t) => alpha(t.palette.info.main, 0.05) : undefined,
                  }}>
                    <TableCell sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                      {i + 1}
                      {isNew && <Chip size="small" label="NEW" color="info"
                        sx={{ ml: 0.5, height: 14, fontSize: 8.5, fontWeight: 800 }} />}
                    </TableCell>
                    <TableCell>
                      <TextField size="small" value={r.name}
                        placeholder="e.g. Mathematics"
                        onChange={(e) => setRow(r._key, { name: e.target.value })}
                        fullWidth
                        InputProps={{ sx: { fontWeight: 700 } }} />
                    </TableCell>
                    {withDepartment && (
                      <TableCell>
                        <TextField select size="small" value={r.dept_id || ''}
                          onChange={(e) => setRow(r._key, { dept_id: e.target.value || null })}
                          fullWidth>
                          <MenuItem value=""><em>— None —</em></MenuItem>
                          {depts.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                        </TextField>
                      </TableCell>
                    )}
                    <TableCell>
                      <TextField size="small" value={r.notes}
                        placeholder="Optional notes / category"
                        onChange={(e) => setRow(r._key, { notes: e.target.value })}
                        fullWidth />
                    </TableCell>
                    <TableCell align="center">
                      <Switch size="small" checked={!!r.is_active}
                        onChange={(e) => setRow(r._key, { is_active: e.target.checked ? 1 : 0 })} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={isNew ? 'Discard this new row' : `Archive ${singular}`}>
                        <IconButton size="small" color="error" onClick={() => removeRow(r._key)}>
                          <DelIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* ── Sticky save bar ─────────────────────────────────── */}
      <Box sx={{
        position: 'sticky', bottom: 0, mt: 1, mx: -1,
        px: 2, py: 1.2, bgcolor: 'background.paper',
        borderTop: 2, borderColor: dirty ? 'warning.main' : 'divider',
        display: 'flex', alignItems: 'center', gap: 1.5,
        boxShadow: '0 -4px 12px rgba(0,0,0,0.04)',
        borderRadius: '0 0 8px 8px',
      }}>
        <Box sx={{
          width: 10, height: 10, borderRadius: '50%',
          bgcolor: dirty ? 'warning.main' : 'success.main', flexShrink: 0,
        }} />
        <Typography variant="body2" sx={{ fontWeight: 700, color: dirty ? 'warning.dark' : 'text.secondary' }}>
          {dirty ? 'Unsaved changes' : `All ${title.toLowerCase()} saved`}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button onClick={load} disabled={saving} sx={{ textTransform: 'none' }}>Discard</Button>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={save}
          disabled={saving || !dirty}
          sx={{ textTransform: 'none', fontWeight: 800, px: 3 }}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </Box>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast?.severity || 'info'} variant="filled" onClose={() => setToast(null)}>
          {toast?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// Drop volatile / UI-only keys when comparing snapshots for dirty
// detection — react internal `_key` and the `_orig` snapshot itself
// should never count as "changed".
function stripVolatile(r) {
  const { _key, _orig, ...rest } = r;
  return rest;
}

// ──────────────────────────────────────────────────────────────────
// Tab 5 — Allowance types
// Each row carries its own taxable / pensionable flag so the payroll
// engine can decide per allowance whether to include it in the SSNIT
// base, the PAYE base, both, or neither.
// ──────────────────────────────────────────────────────────────────
const ALLOW_BLANK = {
  name: '', description: '', default_amount: 0,
  calc_mode: 'amount', default_percentage: 0,
  is_taxable: true, is_pensionable: false, sort_order: 0,
};
const ALLOWANCE_PREVIEW_BASIC = 5000;   // sample basic for the live preview

function AllowanceTypes() {
  const [rows, setRows]   = useState([]);
  const [loading, setL]   = useState(true);
  const [open, setOpen]   = useState(false);
  const [editing, setE]   = useState(null);
  const [form, setForm]   = useState(ALLOW_BLANK);
  const [err, setErr]     = useState('');
  const [q, setQ]         = useState('');
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setL(true);
    try {
      const r = await API.get('/hr/allowance-types', { params: { includeInactive: 1 } });
      setRows(r.data || []);
    } finally { setL(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add  = () => {
    setE(null);
    setForm({ ...ALLOW_BLANK,
      sort_order: (rows[rows.length - 1]?.sort_order || 0) + 10 });
    setErr(''); setOpen(true);
  };
  const edit = (r) => {
    setE(r);
    setForm({
      name: r.name, description: r.description || '',
      default_amount: r.default_amount,
      calc_mode: r.calc_mode === 'percentage' ? 'percentage' : 'amount',
      default_percentage: r.default_percentage ?? 0,
      is_taxable: !!r.is_taxable, is_pensionable: !!r.is_pensionable,
      sort_order: r.sort_order || 0,
    });
    setErr(''); setOpen(true);
  };

  const save = async () => {
    if (!form.name?.trim()) { setErr('Name required'); return; }
    try {
      const payload = { ...form,
        default_amount:     Number(form.default_amount)     || 0,
        default_percentage: Number(form.default_percentage) || 0,
      };
      if (editing) await API.put(`/hr/allowance-types/${editing.id}`, payload);
      else         await API.post('/hr/allowance-types',                payload);
      setOpen(false);
      setToast({ msg: editing ? 'Allowance updated' : 'Allowance added', severity: 'success' });
      await load();
    } catch (e) { setErr(e.response?.data?.message || 'Save failed'); }
  };

  const archive = async (r) => {
    const ok = await swal.confirm({
      title: 'Archive allowance', text: `Hide "${r.name}" from active list?`, tone: 'warning',
    });
    if (!ok) return;
    try {
      await API.delete(`/hr/allowance-types/${r.id}`);
      setToast({ msg: 'Allowance archived', severity: 'success' });
      await load();
    }
    catch (e) { swal.error(e.response?.data?.message || 'Failed'); }
  };

  // Filter + stats
  const visible = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter(r =>
      (r.name || '').toLowerCase().includes(t) ||
      (r.description || '').toLowerCase().includes(t)
    );
  }, [rows, q]);

  const stats = useMemo(() => {
    const active = rows.filter(r => r.is_active);
    return {
      active:     active.length,
      taxable:    active.filter(r => r.is_taxable).length,
      pension:    active.filter(r => r.is_pensionable).length,
      percentage: active.filter(r => r.calc_mode === 'percentage').length,
    };
  }, [rows]);

  // Live preview used in the dialog footer.
  const dialogPreview = useMemo(() => allowancePreview(form), [form]);

  return (
    <Box>
      {/* ── Header stats strip ──────────────────────────────── */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 2,
        bgcolor: (t) => alpha(t.palette.primary.main, 0.03) }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AllowanceIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography sx={{ fontWeight: 800, fontSize: 12, textTransform: 'uppercase',
              letterSpacing: 0.7, color: 'text.secondary' }}>
              Allowance catalogue
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
              <ConfigChip label="Active"     value={String(stats.active)}     tone="success" />
              <ConfigChip label="Taxable"    value={String(stats.taxable)}    tone="warning" />
              <ConfigChip label="Pensionable" value={String(stats.pension)}    tone="info" />
              <ConfigChip label="% of basic" value={String(stats.percentage)} tone="secondary" />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* ── Toolbar ─────────────────────────────────────────── */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
        <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5,
          borderBottom: 1, borderColor: 'divider', bgcolor: '#fafbfc' }}>
          <TextField size="small" placeholder="Search allowance types…"
            value={q} onChange={(e) => setQ(e.target.value)}
            sx={{ flex: 1, maxWidth: 360 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><BracketsIcon sx={{ fontSize: 16, color: 'text.disabled' }} /></InputAdornment>,
              endAdornment: q ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setQ('')}><DelIcon sx={{ fontSize: 14 }} /></IconButton>
                </InputAdornment>
              ) : null,
            }} />
          <Box sx={{ flex: 1 }} />
          <Button startIcon={<AddIcon />} variant="contained" onClick={add}
            sx={{ textTransform: 'none', fontWeight: 800 }}>
            Add allowance
          </Button>
        </Box>

        {/* ── Card grid (more scannable than a wide table) ──── */}
        <Box sx={{ p: 2 }}>
          {loading && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={22} /></Box>}
          {!loading && visible.length === 0 && (
            <Box sx={{ py: 5, textAlign: 'center', color: 'text.disabled', fontStyle: 'italic' }}>
              <AllowanceIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
              <Typography>
                {q ? `No allowances match "${q}".` : 'No allowance types yet. Click Add allowance to create the first one.'}
              </Typography>
            </Box>
          )}
          {!loading && visible.length > 0 && (
            <Grid container spacing={1.4}>
              {visible.map(r => (
                <Grid item xs={12} sm={6} md={4} key={r.id}>
                  <AllowanceCard row={r} onEdit={() => edit(r)} onArchive={() => archive(r)} />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Card>

      {/* ── Dialog ──────────────────────────────────────────── */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}>
        {/* Gradient header */}
        <Box sx={{
          px: 3, py: 2,
          background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
          color: '#fff',
          display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 1.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: 'rgba(255,255,255,0.22)',
            border: '1.5px solid rgba(255,255,255,0.3)',
          }}>
            <AllowanceIcon sx={{ color: '#fff' }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 16, lineHeight: 1.2 }} noWrap>
              {editing ? `Edit allowance · ${editing.name}` : 'New allowance type'}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              Define identity, how it's calculated, and how it behaves on payroll
            </Typography>
          </Box>
          <IconButton onClick={() => setOpen(false)} sx={{ color: '#fff' }}>
            <DelIcon />
          </IconButton>
        </Box>

        <DialogContent dividers sx={{ bgcolor: '#fafbfc', p: 2.5 }}>
          {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

          {/* Identity */}
          <AllowSection title="Identity" subtitle="Visible name and an optional description shown to operators">
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={8}>
                <TextField fullWidth size="small" label="Name *"
                  value={form.name}
                  onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Responsibility, Housing, Transport" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" type="number" label="Sort order"
                  value={form.sort_order}
                  onChange={(e) => setForm(f => ({ ...f, sort_order: e.target.value }))}
                  helperText="Lower = higher in list" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Description (optional)"
                  value={form.description}
                  onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                  multiline rows={2}
                  placeholder="e.g. Paid to Heads of Department and Form Masters" />
              </Grid>
            </Grid>
          </AllowSection>

          {/* Calculation method — visual mode picker (two big radio cards) */}
          <AllowSection title="Calculation method" subtitle="Pick how the cedi value is derived for each employee">
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <ModeCard
                  active={form.calc_mode === 'amount'}
                  onClick={() => setForm(f => ({ ...f, calc_mode: 'amount' }))}
                  tone="primary"
                  icon={<SalaryIcon />}
                  title="Fixed amount (GHS)"
                  desc="Same cedi figure for every employee. Edit per-employee on assign."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ModeCard
                  active={form.calc_mode === 'percentage'}
                  onClick={() => setForm(f => ({ ...f, calc_mode: 'percentage' }))}
                  tone="secondary"
                  icon={<PayeIcon />}
                  title="Percentage of basic"
                  desc="Scales with each employee's basic salary. Updates if basic changes."
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                {form.calc_mode === 'percentage' ? (
                  <TextField fullWidth size="small" type="number"
                    label="Default percentage" value={form.default_percentage}
                    onChange={(e) => setForm(f => ({ ...f, default_percentage: e.target.value }))}
                    inputProps={{ min: 0, step: '0.01' }}
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    helperText="e.g. 15 — applied to each employee's basic salary on assign" />
                ) : (
                  <TextField fullWidth size="small" type="number"
                    label="Default amount" value={form.default_amount}
                    onChange={(e) => setForm(f => ({ ...f, default_amount: e.target.value }))}
                    inputProps={{ min: 0, step: '0.01' }}
                    InputProps={{ startAdornment: <InputAdornment position="start">GHS</InputAdornment> }}
                    helperText="Pre-filled when this allowance is assigned to an employee" />
                )}
              </Grid>

              {/* Live preview against a sample 5000 basic */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 1.5, borderRadius: 1.5,
                  bgcolor: (t) => alpha(t.palette.success.main, 0.08),
                  border: '1px dashed', borderColor: 'success.light',
                  height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center',
                }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'success.dark',
                    textTransform: 'uppercase', letterSpacing: 0.6 }}>
                    Live example
                  </Typography>
                  <Typography sx={{ fontWeight: 800, color: 'success.dark', fontSize: 18, mt: 0.4 }}>
                    GHS {fmtGhsN(dialogPreview)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    On a sample basic of GHS {fmtGhsN(ALLOWANCE_PREVIEW_BASIC)}
                    {form.calc_mode === 'percentage' &&
                      ` (${num(form.default_percentage).toFixed(2)}%)`}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </AllowSection>

          {/* Tax / pension treatment — two big toggle cards */}
          <AllowSection title="Tax & pension treatment" subtitle="Drives whether this allowance enters the PAYE base, the SSNIT base, both, or neither">
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <FlagCard
                  active={!!form.is_taxable}
                  onClick={() => setForm(f => ({ ...f, is_taxable: !f.is_taxable }))}
                  tone="warning"
                  title="Taxable"
                  desc="When ON, the cedi value is added to the PAYE base (employee pays income tax on it)."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FlagCard
                  active={!!form.is_pensionable}
                  onClick={() => setForm(f => ({ ...f, is_pensionable: !f.is_pensionable }))}
                  tone="info"
                  title="Pensionable"
                  desc="When ON, the cedi value is added to the SSNIT base (5.5% employee / 13% employer)."
                />
              </Grid>
            </Grid>
          </AllowSection>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.8, bgcolor: 'background.paper',
          borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={() => setOpen(false)} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button variant="contained" onClick={save} size="large"
            startIcon={<SaveIcon />}
            sx={{ textTransform: 'none', fontWeight: 800, px: 3 }}>
            {editing ? 'Save changes' : 'Create allowance'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast?.severity || 'info'} variant="filled" onClose={() => setToast(null)}>
          {toast?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ──────────────────────────────────────────────────────────────────
// Allowance helpers — card, mode card, flag card, section, preview
// ──────────────────────────────────────────────────────────────────
function allowancePreview(form) {
  if (form.calc_mode === 'percentage') {
    return (ALLOWANCE_PREVIEW_BASIC * (num(form.default_percentage) / 100));
  }
  return num(form.default_amount);
}
const fmtGhsN = (n) => Number(n || 0).toLocaleString('en-GH',
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function AllowSection({ title, subtitle, children }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
        <Typography sx={{ fontWeight: 800, fontSize: 13, textTransform: 'uppercase',
          letterSpacing: 0.7, color: 'text.secondary' }}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
      </Box>
      <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider',
        bgcolor: '#fff' }}>
        {children}
      </Box>
    </Box>
  );
}

function ModeCard({ active, onClick, tone = 'primary', icon, title, desc }) {
  return (
    <Box onClick={onClick} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
      sx={{
        p: 1.5, borderRadius: 2,
        border: '2px solid',
        borderColor: active ? `${tone}.main` : 'divider',
        bgcolor: active ? (t) => alpha(t.palette[tone].main, 0.08) : '#fafbfc',
        cursor: 'pointer',
        transition: 'all 0.15s',
        height: '100%',
        '&:hover': { borderColor: `${tone}.main` },
        display: 'flex', gap: 1.2, alignItems: 'flex-start',
      }}>
      <Box sx={{
        width: 40, height: 40, borderRadius: 1.5, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: active ? `${tone}.main` : (t) => alpha(t.palette[tone].main, 0.12),
        color: active ? '#fff' : `${tone}.main`,
      }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 800, fontSize: 13.5, color: active ? `${tone}.dark` : 'text.primary' }}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
          {desc}
        </Typography>
      </Box>
      <Box sx={{ flexShrink: 0 }}>
        <Box sx={{
          width: 18, height: 18, borderRadius: '50%',
          border: '2px solid', borderColor: active ? `${tone}.main` : 'divider',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: active ? `${tone}.main` : 'transparent',
        }}>
          {active && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#fff' }} />}
        </Box>
      </Box>
    </Box>
  );
}

function FlagCard({ active, onClick, tone = 'warning', title, desc }) {
  return (
    <Box onClick={onClick} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
      sx={{
        p: 1.5, borderRadius: 2,
        border: '2px solid',
        borderColor: active ? `${tone}.main` : 'divider',
        bgcolor: active ? (t) => alpha(t.palette[tone].main, 0.08) : '#fafbfc',
        cursor: 'pointer',
        transition: 'all 0.15s',
        height: '100%',
        '&:hover': { borderColor: `${tone}.main` },
        display: 'flex', gap: 1.2, alignItems: 'flex-start',
      }}>
      <Switch checked={active}
        onClick={(e) => e.stopPropagation()}
        onChange={onClick}
        color={tone === 'warning' ? 'warning' : tone === 'info' ? 'info' : 'primary'} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={0.8} alignItems="center">
          <Typography sx={{ fontWeight: 800, fontSize: 13.5, color: active ? `${tone}.dark` : 'text.primary' }}>
            {title}
          </Typography>
          <Chip size="small"
            label={active ? 'YES' : 'NO'}
            color={active ? tone : 'default'}
            variant={active ? 'filled' : 'outlined'}
            sx={{ fontWeight: 800, fontSize: 9.5, height: 16 }} />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4, mt: 0.3 }}>
          {desc}
        </Typography>
      </Box>
    </Box>
  );
}

function AllowanceCard({ row, onEdit, onArchive }) {
  const r = row;
  const isPct = r.calc_mode === 'percentage';
  return (
    <Card variant="outlined" sx={{
      borderRadius: 2, height: '100%',
      borderLeft: 4,
      borderColor: r.is_active
        ? (isPct ? 'secondary.main' : 'primary.main')
        : 'grey.300',
      transition: 'box-shadow 0.15s',
      '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
    }}>
      <CardContent sx={{ p: 1.8, '&:last-child': { pb: 1.8 } }}>
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 14.5, lineHeight: 1.2 }} noWrap>
              {r.name}
            </Typography>
            {r.description && (
              <Typography variant="caption" color="text.secondary"
                sx={{ display: 'block', mt: 0.2, lineHeight: 1.35 }}>
                {r.description}
              </Typography>
            )}
          </Box>
          {!r.is_active && (
            <Chip size="small" label="Archived"
              sx={{ fontWeight: 800, fontSize: 9.5, height: 18 }} />
          )}
        </Stack>

        <Box sx={{ mt: 1.2, mb: 1.2, p: 1, borderRadius: 1.5,
          bgcolor: (t) => alpha(t.palette[isPct ? 'secondary' : 'primary'].main, 0.06),
          textAlign: 'center',
        }}>
          <Typography variant="caption" sx={{ fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: 0.5,
            color: isPct ? 'secondary.dark' : 'primary.dark', fontSize: 9.5 }}>
            {isPct ? 'Percentage of basic' : 'Fixed amount'}
          </Typography>
          <Typography sx={{ fontWeight: 800, fontSize: 17,
            color: isPct ? 'secondary.dark' : 'primary.dark', lineHeight: 1.2 }}>
            {isPct
              ? `${Number(r.default_percentage || 0).toFixed(2)} %`
              : `GHS ${Number(r.default_amount || 0).toFixed(2)}`}
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.5} alignItems="center">
          <Chip size="small"
            label="Taxable"
            color={r.is_taxable ? 'warning' : 'default'}
            variant={r.is_taxable ? 'filled' : 'outlined'}
            sx={{ fontWeight: 700, fontSize: 10, height: 20 }} />
          <Chip size="small"
            label="Pensionable"
            color={r.is_pensionable ? 'info' : 'default'}
            variant={r.is_pensionable ? 'filled' : 'outlined'}
            sx={{ fontWeight: 700, fontSize: 10, height: 20 }} />
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Edit">
            <IconButton size="small" onClick={onEdit}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {!!r.is_active && (
            <Tooltip title="Archive">
              <IconButton size="small" color="error" onClick={onArchive}>
                <DelIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
